// src/pages/Catalog/ServicePage.jsx
//
// Service create/edit page
// ========================
//
// PURPOSE
// -------
// This page owns service creation and service editing.
// ServiceOverviewPage.jsx owns list, filters and quick actions.
//
// IMPORTANT RETURN-TO MODEL
// -------------------------
// This page supports the same return-to pattern as ProductPage and VoucherPage.
//
// ServiceOverviewPage may navigate here with:
//
//   state: {
//     service,
//     phase: "edit",
//     returnTo: "/catalog/services?q=masa%C5%BC&page=2&pageSize=50"
//   }
//
// This page then uses:
//
//   const backToOverviewPath = location.state?.returnTo || "/catalog/services";
//
// Result:
// - If opened from ServiceOverviewPage, Back/Cancel returns to the exact previous
//   overview state.
// - If opened from Command Search or a direct URL, fallback remains the normal
//   overview route.
// - No extra button is created. Existing Back to overview and Cancel links are
//   reused.
//
// IMPORTANT NAMING NOTE
// ---------------------
// This file is the service equivalent of ProductPage.jsx:
//
//   ProductPage.jsx = create/edit one product
//   ServicePage.jsx = create/edit one service
//
// SERVICE MODEL
// -------------
// Backend target model:
//
//   backend/models/service.js
//   table: services
//
// Not catalog_services.
//
// LEGACY CLEANUP
// --------------
// This page assumes the target service model has been cleaned up:
//
// Removed:
// - name_internal
// - legacy price as source of truth
//
// Replaced by:
// - name_public
// - price_entry_mode
// - price_input
// - vat_rate_percent
// - price_net
// - price_vat
// - price_gross
//
// GENERIC MEDIA MODEL
// -------------------
// Service images are stored in generic media_files:
//
//   owner_type = "service"
//   owner_id   = service.id
//   media_role = "gallery"
//
// In CREATE mode:
// - selected files are staged locally
// - local image previews are displayed immediately
// - after service creation, files are uploaded to generic media endpoint
//
// In EDIT mode:
// - selected files are uploaded immediately
// - after upload, service data is refreshed and existing gallery is re-rendered
//
// IMPORTANT LOCAL PREVIEW NOTE
// ----------------------------
// Do not create URL.createObjectURL(...) directly in render or useMemo.
// Keep object URLs in state and revoke them explicitly when pending files change
// or when the component unmounts. This avoids subtle browser preview issues.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./ServicePage.css";

import {
  createCatalogService,
  fetchCatalogServiceById,
  updateCatalogService,
} from "../../services/servicesCatalogService";

import { fetchServiceMainCategories } from "../../services/serviceMainCategoriesService";
import { fetchServiceSections } from "../../services/serviceSectionsService";

import {
  deleteOwnerMedia,
  setPrimaryOwnerMedia,
  uploadOwnerMedia,
} from "../../services/mediaFileService";

import MediaFileImage from "../../components/media/MediaFileImage";
import ServiceMaterialsPanel from "./ServiceMaterialsPanel";

const PRICE_MODE_GROSS = "GROSS_INCLUDES_VAT";
const PRICE_MODE_NET = "NET_PLUS_VAT";

const MEDIA_OWNER_SERVICE = "service";
const MEDIA_ROLE_GALLERY = "gallery";

function asBool(v) {
  return (
    v === true ||
    String(v).trim().toLowerCase() === "true" ||
    String(v).trim() === "1"
  );
}

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === "") return null;

  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function roundMoney(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function money(value) {
  if (value === null || value === undefined || value === "") return "—";

  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  return n.toFixed(2);
}

function calculatePricingPreview(form) {
  const input = toNumberOrNull(form.price_input);
  const vatRate = toNumberOrNull(form.vat_rate_percent) ?? 0;

  if (input == null || input < 0 || vatRate < 0) {
    return { net: null, vat: null, gross: null };
  }

  if (form.price_entry_mode === PRICE_MODE_NET) {
    const net = roundMoney(input);
    const vat = roundMoney(net * (vatRate / 100));
    const gross = roundMoney(net + vat);

    return { net, vat, gross };
  }

  const gross = roundMoney(input);
  const net = vatRate === 0 ? gross : roundMoney(gross / (1 + vatRate / 100));
  const vat = roundMoney(gross - net);

  return { net, vat, gross };
}

function initialForm() {
  return {
    section_id: "",
    name_public: "",
    description_short: "",
    duration_minutes: "",
    price_label: "",
    price_entry_mode: PRICE_MODE_GROSS,
    price_input: "",
    vat_rate_percent: "0",
    sort_order: 0,
    is_active: true,
    publish_to_website: true,
  };
}

function mapServiceToForm(row) {
  return {
    section_id: row?.section_id ? String(row.section_id) : "",
    name_public: row?.name_public || "",
    description_short: row?.description_short || "",
    duration_minutes: row?.duration_minutes ?? "",
    price_label: row?.price_label || "",
    price_entry_mode: row?.price_entry_mode || PRICE_MODE_GROSS,
    price_input: row?.price_input ?? "",
    vat_rate_percent: row?.vat_rate_percent ?? "0",
    sort_order: Number(row?.sort_order || 0),
    is_active: asBool(row?.is_active),
    publish_to_website: asBool(row?.publish_to_website),
  };
}

function createPayload(form) {
  return {
    section_id: Number(form.section_id),
    name_public: form.name_public,
    description_short: form.description_short,
    duration_minutes:
      form.duration_minutes === "" ? null : Number(form.duration_minutes),
    price_label: form.price_label,
    price_entry_mode: form.price_entry_mode,
    price_input: form.price_input === "" ? null : form.price_input,
    vat_rate_percent: form.vat_rate_percent === "" ? 0 : form.vat_rate_percent,
    sort_order: Number(form.sort_order || 0),
    is_active: !!form.is_active,
    publish_to_website: !!form.publish_to_website,
  };
}

function imageList(service) {
  return Array.isArray(service?.images) ? service.images : [];
}

function getPrimaryImage(service) {
  const images = imageList(service);
  if (!images.length) return null;

  return images.find((img) => asBool(img.is_primary)) || images[0];
}

function fileSizeKb(file) {
  const size = Number(file?.size || 0);
  if (!Number.isFinite(size)) return "";
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function formatSectionName(sections, sectionId) {
  const row = sections.find((s) => String(s.id) === String(sectionId));
  if (!row) return "—";

  const mainName =
    row.mainCategory?.name_public ||
    row.main_category?.name_public ||
    "";

  return mainName ? `${mainName} / ${row.name_public}` : row.name_public;
}

export default function ServicePage() {
  const { t } = useTranslation(["catalog", "common"]);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const fileInputRef = useRef(null);

  const isCreate = !id || id === "new";

  /**
   * Return target for Back to overview and Cancel.
   *
   * If ServicePage was opened from ServiceOverviewPage, this contains the exact
   * filtered overview URL. If the page was opened directly or from Command
   * Search, fallback stays the normal overview path.
   */
  const backToOverviewPath = location.state?.returnTo || "/catalog/services";

  const [mainCategories, setMainCategories] = useState([]);
  const [sections, setSections] = useState([]);

  const [service, setService] = useState(location.state?.service || null);

  const [form, setForm] = useState(
    location.state?.service
      ? mapServiceToForm(location.state.service)
      : initialForm()
  );

  const [selectedMainCategoryId, setSelectedMainCategoryId] = useState("");

  // Files selected in CREATE mode before the service has an ID.
  const [pendingFiles, setPendingFiles] = useState([]);

  // Local browser preview URLs for pendingFiles.
  // Kept in state intentionally to avoid creating object URLs during render.
  const [pendingFilePreviews, setPendingFilePreviews] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const pricingPreview = useMemo(() => calculatePricingPreview(form), [form]);

  const primaryImage = useMemo(() => getPrimaryImage(service), [service]);

  const visibleSections = useMemo(() => {
    if (!selectedMainCategoryId) return sections;

    return sections.filter(
      (s) => String(s.main_category_id) === String(selectedMainCategoryId)
    );
  }, [sections, selectedMainCategoryId]);

  /**
   * Build and clean local preview URLs.
   *
   * This is the key difference from a fragile useMemo approach:
   * - URLs are created only when pendingFiles changes.
   * - URLs are revoked when pendingFiles changes again or when page unmounts.
   * - The render tree receives stable preview objects from state.
   */
  useEffect(() => {
    if (!pendingFiles.length) {
      setPendingFilePreviews([]);
      return undefined;
    }

    const previews = pendingFiles.map((file, index) => ({
      file,
      index,
      url: URL.createObjectURL(file),
    }));

    setPendingFilePreviews(previews);

    return () => {
      previews.forEach((item) => {
        try {
          URL.revokeObjectURL(item.url);
        } catch {
          // Ignore browser cleanup errors.
        }
      });
    };
  }, [pendingFiles]);

  /**
   * Auto-hide success messages.
   *
   * UX reason:
   * - success feedback confirms the action,
   * - after a short moment it should disappear automatically,
   * - errors stay visible because they usually require user action.
   */
  useEffect(() => {
    if (!success) return undefined;

    const timer = window.setTimeout(() => {
      setSuccess("");
    }, 3500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [success]);

  async function loadDictionaries() {
    const [cats, secs] = await Promise.all([
      fetchServiceMainCategories({ active: true }),
      fetchServiceSections({ active: true }),
    ]);

    setMainCategories(Array.isArray(cats) ? cats : []);
    setSections(Array.isArray(secs) ? secs : []);
  }

  async function loadService(serviceId) {
    const row = await fetchCatalogServiceById(serviceId);

    setService(row);
    setForm(mapServiceToForm(row || {}));

    const mainId =
      row?.section?.main_category_id ||
      row?.section?.mainCategory?.id ||
      row?.section?.main_category?.id ||
      "";

    if (mainId) {
      setSelectedMainCategoryId(String(mainId));
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError("");

      try {
        await loadDictionaries();

        if (!isCreate) {
          const row = await fetchCatalogServiceById(id);
          if (cancelled) return;

          setService(row);
          setForm(mapServiceToForm(row || {}));

          const mainId =
            row?.section?.main_category_id ||
            row?.section?.mainCategory?.id ||
            row?.section?.main_category?.id ||
            "";

          if (mainId) {
            setSelectedMainCategoryId(String(mainId));
          }
        } else if (!cancelled) {
          setService(null);
          setForm(initialForm());
          setPendingFiles([]);
          setSelectedMainCategoryId("");
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || t("catalog:servicePage.messages.initializeFailed"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [id, isCreate]);

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleMainCategoryChange(value) {
    setSelectedMainCategoryId(value);

    // Reset section when category helper changes.
    // The old selected section may belong to another category.
    setField("section_id", "");
  }

  function addPendingFiles(fileList) {
    const next = Array.from(fileList || []).filter(Boolean);

    if (!next.length) return;

    setPendingFiles((prev) => [...prev, ...next]);
    setSuccess("");
    setError("");
  }

  function removePendingFile(index) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function uploadFilesForService(serviceId, files) {
    const fileList = Array.from(files || []).filter(Boolean);
    if (!fileList.length) return;

    setUploading(true);

    try {
      await uploadOwnerMedia(MEDIA_OWNER_SERVICE, serviceId, fileList, {
        media_role: MEDIA_ROLE_GALLERY,
      });

      setPendingFiles([]);

      const refreshed = await fetchCatalogServiceById(serviceId);
      setService(refreshed);
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = createPayload(form);
      let saved;

      if (isCreate) {
        saved = await createCatalogService(payload);
        setSuccess(t("catalog:servicePage.messages.created"));
      } else {
        saved = await updateCatalogService(id, payload);
        setSuccess(t("catalog:servicePage.messages.updated"));
      }

      const serviceId = saved?.id || id;

      if (serviceId && pendingFiles.length > 0) {
        await uploadFilesForService(serviceId, pendingFiles);
      }

      if (isCreate && serviceId) {
        /**
         * Keep the existing create flow:
         * after creation we open the created service in edit mode.
         *
         * Important:
         * we preserve returnTo in navigation state so that Back to overview and
         * Cancel still know the original filtered overview.
         */
        navigate(`/catalog/services/${serviceId}`, {
          replace: true,
          state: {
            returnTo: backToOverviewPath,
            phase: "edit",
          },
        });
      } else if (serviceId) {
        await loadService(serviceId);
      }
    } catch (e2) {
      setError(e2?.message || t("catalog:servicePage.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function handleImmediateUpload(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    // CREATE mode:
    // No service ID exists yet, so we only stage files locally and show previews.
    if (isCreate) {
      addPendingFiles(files);
      return;
    }

    // EDIT mode:
    // Service already exists, so selected files can be uploaded immediately.
    setError("");
    setSuccess("");

    try {
      await uploadFilesForService(id, files);
      setSuccess(t("catalog:servicePage.messages.imageUploaded"));
    } catch (e) {
      setError(e?.message || t("catalog:servicePage.messages.imageUploadFailed"));
    }
  }

  function handleFilePickerChange(e) {
    // Copy FileList immediately before clearing the input.
    // FileList is browser-managed and should not be accessed later after reset.
    const files = Array.from(e.target.files || []).filter(Boolean);

    handleImmediateUpload(files);

    // Allows selecting the same file again.
    e.target.value = "";
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    setDragActive(false);

    const files = Array.from(e.dataTransfer.files || []).filter(Boolean);
    handleImmediateUpload(files);
  }

  async function handleSetPrimary(mediaId) {
    setError("");
    setSuccess("");

    try {
      await setPrimaryOwnerMedia(MEDIA_OWNER_SERVICE, id, mediaId, {
        media_role: MEDIA_ROLE_GALLERY,
      });

      await loadService(id);
      setSuccess(t("catalog:servicePage.messages.primaryUpdated"));
    } catch (e) {
      setError(e?.message || t("catalog:servicePage.messages.primaryUpdateFailed"));
    }
  }

  async function handleDeleteImage(mediaId) {
    setError("");
    setSuccess("");

    const ok = window.confirm(t("catalog:servicePage.confirmDeleteImage"));
    if (!ok) return;

    try {
      await deleteOwnerMedia(MEDIA_OWNER_SERVICE, id, mediaId, {
        media_role: MEDIA_ROLE_GALLERY,
      });

      await loadService(id);
      setSuccess(t("catalog:servicePage.messages.imageDeleted"));
    } catch (e) {
      setError(e?.message || t("catalog:servicePage.messages.imageDeleteFailed"));
    }
  }

  const pageModeLabel = isCreate
    ? t("catalog:servicePage.mode.create")
    : t("catalog:servicePage.mode.edit");

  const saveLabel =
    saving || uploading
      ? t("catalog:servicePage.actions.saving")
      : isCreate
      ? t("catalog:servicePage.actions.createService")
      : t("catalog:servicePage.actions.saveChanges");

  return (
    <div className="svc-page">
      <div className="svc-page__inner svc-page__inner--narrow">
        <header className="svc-header">
          <div className="svc-header__copy">
            <div className="svc-kicker">
              <span>{pageModeLabel}</span>

              <span
                className={
                  form.is_active ? "svc-badge svc-badge--green" : "svc-badge"
                }
              >
                {form.is_active
                  ? t("catalog:servicePage.fields.active")
                  : t("common:status.inactive")}
              </span>

              {form.publish_to_website ? (
                <span className="svc-badge svc-badge--blue">{t("catalog:servicePage.badges.website")}</span>
              ) : (
                <span className="svc-badge">{t("catalog:servicePage.badges.notPublished")}</span>
              )}
            </div>

            <h1>{isCreate ? t("catalog:servicePage.title.add") : t("catalog:servicePage.title.edit")}</h1>

            <p>
              {t("catalog:servicePage.subtitle")}
            </p>
          </div>

          <div className="svc-header__actions">
            <Link className="svc-btn svc-btn--soft" to={backToOverviewPath}>
              {t("catalog:servicePage.actions.backToOverview")}
            </Link>
          </div>
        </header>

        {loading ? (
          <section className="svc-loadingCard">
            <div className="svc-loaderDot" />
            <div>
              <strong>{t("catalog:servicePage.loading.title")}</strong>
              <p>{t("catalog:servicePage.loading.description")}</p>
            </div>
          </section>
        ) : (
          <form className="svc-formShell" onSubmit={handleSubmit}>
            <div className="svc-formBody">
              {error ? (
                <div className="svc-alert svc-alert--error">{error}</div>
              ) : null}

              {success ? (
                <div className="svc-alert svc-alert--success">{success}</div>
              ) : null}

              <div className="svc-contentGrid">
                <main className="svc-mainColumn">
                  <section className="svc-section svc-section--hero">
                    <div className="svc-section__title">
                      <span className="svc-sectionIcon">01</span>
                      <div>
                        <h2>{t("catalog:servicePage.sections.basic.title")}</h2>
                        <p>
                          {t("catalog:servicePage.sections.basic.description")}
                        </p>
                      </div>
                    </div>

                    <div className="svc-grid">
                      <div className="svc-field">
                        <label>{t("catalog:servicePage.fields.mainCategory")}</label>
                        <select
                          value={selectedMainCategoryId}
                          onChange={(e) =>
                            handleMainCategoryChange(e.target.value)
                          }
                        >
                          <option value="">{t("catalog:servicePage.options.allCategories")}</option>
                          {mainCategories.map((cat) => (
                            <option key={cat.id} value={String(cat.id)}>
                              {cat.name_public}
                            </option>
                          ))}
                        </select>
                        <small>{t("catalog:servicePage.hints.mainCategoryFilter")}</small>
                      </div>

                      <div className="svc-field">
                        <label>{t("catalog:servicePage.fields.sectionRequired")}</label>
                        <select
                          value={form.section_id}
                          onChange={(e) =>
                            setField("section_id", e.target.value)
                          }
                          required
                        >
                          <option value="">{t("catalog:servicePage.options.selectSection")}</option>
                          {visibleSections.map((section) => (
                            <option key={section.id} value={String(section.id)}>
                              {section.mainCategory?.name_public
                                ? `${section.mainCategory.name_public} / `
                                : ""}
                              {section.name_public}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="svc-field svc-field--full">
                        <label>{t("catalog:servicePage.fields.serviceNameRequired")}</label>
                        <input
                          value={form.name_public}
                          onChange={(e) =>
                            setField("name_public", e.target.value)
                          }
                          placeholder={t("catalog:servicePage.placeholders.serviceName")}
                          required
                        />
                      </div>

                      <div className="svc-field svc-field--full">
                        <label>{t("catalog:servicePage.fields.shortDescription")}</label>
                        <textarea
                          rows="3"
                          value={form.description_short}
                          onChange={(e) =>
                            setField("description_short", e.target.value)
                          }
                          placeholder={t("catalog:servicePage.placeholders.shortDescription")}
                        />
                      </div>
                    </div>
                  </section>

                  <section className="svc-section">
                    <div className="svc-section__title">
                      <span className="svc-sectionIcon">02</span>
                      <div>
                        <h2>{t("catalog:servicePage.sections.duration.title")}</h2>
                        <p>
                          {t("catalog:servicePage.sections.duration.description")}
                        </p>
                      </div>
                    </div>

                    <div className="svc-grid">
                      <div className="svc-field">
                        <label>{t("catalog:servicePage.fields.durationMinutes")}</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={form.duration_minutes}
                          onChange={(e) =>
                            setField("duration_minutes", e.target.value)
                          }
                          placeholder={t("catalog:servicePage.placeholders.duration")}
                        />
                      </div>

                      <div className="svc-field">
                        <label>{t("catalog:servicePage.fields.sortOrder")}</label>
                        <input
                          type="number"
                          value={form.sort_order}
                          onChange={(e) =>
                            setField("sort_order", e.target.value)
                          }
                        />
                      </div>

                      <div className="svc-field svc-field--full">
                        <label>{t("catalog:servicePage.fields.priceLabel")}</label>
                        <input
                          value={form.price_label}
                          onChange={(e) =>
                            setField("price_label", e.target.value)
                          }
                          placeholder={t("catalog:servicePage.placeholders.priceLabel")}
                        />
                      </div>
                    </div>
                  </section>

                  <section className="svc-section">
                    <div className="svc-section__title">
                      <span className="svc-sectionIcon">03</span>
                      <div>
                        <h2>{t("catalog:servicePage.sections.pricing.title")}</h2>
                        <p>
                          {t("catalog:servicePage.sections.pricing.description")}
                        </p>
                      </div>
                    </div>

                    <div className="svc-grid">
                      <div className="svc-field">
                        <label>{t("catalog:servicePage.fields.priceEntryMode")}</label>
                        <select
                          value={form.price_entry_mode}
                          onChange={(e) =>
                            setField("price_entry_mode", e.target.value)
                          }
                        >
                          <option value={PRICE_MODE_GROSS}>
                            {t("catalog:servicePage.options.grossIncludesVat")}
                          </option>
                          <option value={PRICE_MODE_NET}>{t("catalog:servicePage.options.netPlusVat")}</option>
                        </select>
                      </div>

                      <div className="svc-field">
                        <label>{t("catalog:servicePage.fields.priceInput")}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.price_input}
                          onChange={(e) =>
                            setField("price_input", e.target.value)
                          }
                          placeholder="0.00"
                        />
                      </div>

                      <div className="svc-field">
                        <label>{t("catalog:servicePage.fields.vatRate")}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={form.vat_rate_percent}
                          onChange={(e) =>
                            setField("vat_rate_percent", e.target.value)
                          }
                        />
                      </div>

                      <div className="svc-pricePreview svc-field--full">
                        <div>
                          <span>{t("catalog:servicePage.pricePreview.net")}</span>
                          <strong>{money(pricingPreview.net)}</strong>
                        </div>

                        <div>
                          <span>{t("catalog:servicePage.pricePreview.vat")}</span>
                          <strong>{money(pricingPreview.vat)}</strong>
                        </div>

                        <div className="svc-pricePreview__main">
                          <span>{t("catalog:servicePage.pricePreview.gross")}</span>
                          <strong>{money(pricingPreview.gross)}</strong>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="svc-section svc-section--media">
                    <div className="svc-section__title">
                      <span className="svc-sectionIcon">04</span>
                      <div>
                        <h2>{t("catalog:servicePage.sections.images.title")}</h2>
                        <p>
                          {t("catalog:servicePage.sections.images.description")}
                        </p>
                      </div>
                    </div>

                    <div
                      className={`svc-dropzone ${
                        dragActive ? "svc-dropzone--active" : ""
                      }`}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                      }}
                      onDrop={handleDrop}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        hidden
                        onChange={handleFilePickerChange}
                      />

                      <div className="svc-dropzone__icon" aria-hidden="true">
                        +
                      </div>

                      <p>{t("catalog:servicePage.images.dragHere")}</p>
                      <span>{t("catalog:servicePage.images.or")}</span>

                      <button
                        type="button"
                        className="svc-btn svc-btn--soft"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {t("catalog:servicePage.actions.selectImages")}
                      </button>

                      <small>{t("catalog:servicePage.images.supportedFormats")}</small>
                    </div>

                    {pendingFilePreviews.length > 0 ? (
                      <div className="svc-pendingFiles">
                        <div className="svc-pendingFiles__header">
                          <strong>{t("catalog:servicePage.images.waitingForSave")}</strong>
                          <span>{t("catalog:servicePage.images.fileCount", { count: pendingFilePreviews.length })}</span>
                        </div>

                        <div className="svc-pendingGrid">
                          {pendingFilePreviews.map((item) => (
                            <div
                              key={`${item.file.name}-${item.index}`}
                              className="svc-pendingFile"
                            >
                              <img src={item.url} alt={item.file.name} />

                              <div>
                                <span>{item.file.name}</span>
                                <small>{fileSizeKb(item.file)}</small>
                              </div>

                              <button
                                type="button"
                                className="svc-btn svc-btn--small"
                                onClick={() => removePendingFile(item.index)}
                              >
                                {t("catalog:servicePage.actions.remove")}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {!isCreate && imageList(service).length > 0 ? (
                      <div className="svc-gallery">
                        {imageList(service).map((img) => (
                          <div key={img.id} className="svc-galleryItem">
                            <MediaFileImage
                              media={img}
                              alt={
                                img.original_name ||
                                service?.name_public ||
                                t("catalog:servicePage.media.serviceAlt")
                              }
                              className="svc-galleryImage"
                              fallbackClassName="svc-galleryImageFallback"
                              emptyText={t("catalog:servicePage.media.noImage")}
                              loadingText={t("catalog:servicePage.media.loading")}
                              failedText={t("catalog:servicePage.media.imageUnavailable")}
                            />

                            <div className="svc-galleryItem__meta">
                              <span>
                                {asBool(img.is_primary)
                                  ? t("catalog:servicePage.media.primary")
                                  : t("catalog:servicePage.media.image")}
                              </span>
                              <small>{img.original_name || img.file_name}</small>
                            </div>

                            <div className="svc-galleryItem__actions">
                              {!asBool(img.is_primary) ? (
                                <button
                                  type="button"
                                  className="svc-btn svc-btn--small"
                                  onClick={() => handleSetPrimary(img.id)}
                                >
                                  {t("catalog:servicePage.actions.setPrimary")}
                                </button>
                              ) : null}

                              <button
                                type="button"
                                className="svc-btn svc-btn--small svc-btn--dangerSoft"
                                onClick={() => handleDeleteImage(img.id)}
                              >
                                {t("catalog:servicePage.actions.delete")}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </section>

                  <section className="svc-section svc-section--compact">
                    <div className="svc-checks">
                      <label>
                        <input
                          type="checkbox"
                          checked={!!form.is_active}
                          onChange={(e) =>
                            setField("is_active", e.target.checked)
                          }
                        />
                        <span>{t("catalog:servicePage.fields.active")}</span>
                      </label>

                      <label>
                        <input
                          type="checkbox"
                          checked={!!form.publish_to_website}
                          onChange={(e) =>
                            setField("publish_to_website", e.target.checked)
                          }
                        />
                        <span>{t("catalog:servicePage.fields.publishToWebsite")}</span>
                      </label>
                    </div>
                  </section>

                  {/*
                    Service material rules / salon BOM.

                    This panel is intentionally shown only after the service has
                    a database id. Creating material rules before service save
                    would require temporary client-side ids and would make the
                    workflow more fragile.
                  */}
                  <ServiceMaterialsPanel
                    serviceId={!isCreate ? service?.id : null}
                    disabled={isCreate || saving}
                  />
                </main>

                <aside className="svc-sidePanel" aria-label={t("catalog:servicePage.summary.aria")}>
                  <div className="svc-summaryCard">
                    <div className="svc-summaryImage">
                      {primaryImage ? (
                        <MediaFileImage
                          media={primaryImage}
                          alt={service?.name_public || t("catalog:servicePage.media.primaryServiceAlt")}
                          className="svc-summaryImage__img"
                          fallbackClassName="svc-summaryImage__empty"
                          emptyText={t("catalog:servicePage.media.noPrimaryImage")}
                          loadingText={t("catalog:servicePage.media.loading")}
                          failedText={t("catalog:servicePage.media.imageUnavailable")}
                        />
                      ) : pendingFilePreviews[0]?.url ? (
                        <img
                          src={pendingFilePreviews[0].url}
                          alt={pendingFilePreviews[0].file.name}
                        />
                      ) : (
                        <div className="svc-summaryImage__empty">
                          {t("catalog:servicePage.media.noPrimaryImage")}
                        </div>
                      )}
                    </div>

                    <div className="svc-summaryContent">
                      <span className="svc-summaryEyebrow">{t("catalog:servicePage.summary.title")}</span>

                      <h3>{form.name_public || t("catalog:servicePage.summary.unnamedService")}</h3>

                      <div className="svc-summaryRows">
                        <div>
                          <span>{t("catalog:servicePage.summary.section")}</span>
                          <strong>
                            {formatSectionName(sections, form.section_id)}
                          </strong>
                        </div>

                        <div>
                          <span>{t("catalog:servicePage.summary.duration")}</span>
                          <strong>
                            {form.duration_minutes
                              ? t("catalog:servicePage.durationMinutes", { value: form.duration_minutes })
                              : "—"}
                          </strong>
                        </div>

                        <div>
                          <span>{t("catalog:servicePage.summary.grossPrice")}</span>
                          <strong>{money(pricingPreview.gross)}</strong>
                        </div>
                      </div>

                      <div className="svc-summaryBadges">
                        <span
                          className={
                            form.is_active
                              ? "svc-badge svc-badge--green"
                              : "svc-badge"
                          }
                        >
                          {form.is_active
                  ? t("catalog:servicePage.fields.active")
                  : t("common:status.inactive")}
                        </span>

                        <span
                          className={
                            form.publish_to_website
                              ? "svc-badge svc-badge--blue"
                              : "svc-badge"
                          }
                        >
                          {form.publish_to_website
                            ? t("catalog:servicePage.fields.publishToWebsite")
                            : t("catalog:servicePage.summary.hiddenFromWebsite")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="svc-helpCard">
                    <h3>{t("catalog:servicePage.tips.title")}</h3>
                    <ul>
                      <li>{t("catalog:servicePage.tips.publicNames")}</li>
                      <li>
                        {t("catalog:servicePage.tips.priceLabel")}
                      </li>
                      <li>
                        {t("catalog:servicePage.tips.duration")}
                      </li>
                      <li>
                        {t("catalog:servicePage.tips.images")}
                      </li>
                    </ul>
                  </div>
                </aside>
              </div>
            </div>

            <footer className="svc-formFooter">
              <div className="svc-formFooter__meta">
                {uploading
                  ? t("catalog:servicePage.footer.uploadingImages")
                  : saving
                  ? t("catalog:servicePage.footer.savingService")
                  : t("catalog:servicePage.footer.ready")}
              </div>

              <div className="svc-formFooter__actions">
                <Link className="svc-btn svc-btn--soft" to={backToOverviewPath}>
                  {t("catalog:servicePage.actions.cancel")}
                </Link>

                <button
                  type="submit"
                  className="svc-btn svc-btn--primary svc-btn--save"
                  disabled={saving || uploading}
                >
                  {saveLabel}
                </button>
              </div>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
}



